import { createContext, useContext, useState, useLayoutEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useStaffLanguageSync } from '../features/account-settings/useStaffLanguageSync';
import { getAccountLanguage, publishAccountLanguage } from '../features/account-settings/preferenceEvents';
import { translateUiText, translateKey, formatUiText, type AppLanguage } from '../ui/locale/centralLocalization';

type Language = AppLanguage;

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    toggleLanguage: () => void;
    t: (text: string) => string;
    key: (key: string, values?: readonly unknown[], fallback?: string) => string;
    format: (template: string, values: readonly unknown[]) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Shared staff widgets also render in isolated tests and standalone documents.
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalLanguage = () => useContext(LanguageContext);

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(() => {
        const accountLanguage = getAccountLanguage();
        if (accountLanguage) return accountLanguage;
        let saved: string | null = null;
        try { saved = localStorage.getItem('app_language'); } catch { /* Storage can be unavailable. */ }
        return (saved === 'en' || saved === 'ar') ? saved : 'en';
    });

    useLayoutEffect(() => {
        try { localStorage.setItem('app_language', language); } catch { /* Keep in-memory switching available. */ }
        document.documentElement.lang = language;
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
        document.body.lang = language;
        document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
        
        // Add/remove class for CSS selectors
        if (language === 'ar') {
            document.body.classList.add('rtl', 'lang-ar');
            document.body.classList.remove('ltr', 'lang-en');
        } else {
            document.body.classList.add('ltr', 'lang-en');
            document.body.classList.remove('rtl', 'lang-ar');
        }
    }, [language]);

    const saveLanguage = useStaffLanguageSync(setLanguageState);
    const setLanguage = useCallback((lang: Language) => {
        saveLanguage(lang);
        publishAccountLanguage(lang);
    }, [saveLanguage]);

    const toggleLanguage = useCallback(() => {
        setLanguage(language === 'en' ? 'ar' : 'en');
    }, [language, setLanguage]);

    const t = useCallback((text: string) => translateUiText(text, language), [language]);
    const key = useCallback((messageKey: string, values?: readonly unknown[], fallback?: string) => translateKey(messageKey, language, values, fallback), [language]);
    const format = useCallback((template: string, values: readonly unknown[]) => formatUiText(template, values, language), [language]);
    const value = useMemo(() => ({ language, setLanguage, toggleLanguage, t, key, format }), [language, setLanguage, toggleLanguage, t, key, format]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}
